import { defineStore } from 'pinia';
import { ref } from 'vue';
import { documentApi } from '@/entities/Document/@x/Notification';

/**
 * Notification Store
 *
 * Manages notification counts for pending approvals, verifications, and CP documents.
 * This is entity-level logic because it deals with document state counts that
 * multiple features (and the navbar widget) need to access.
 */
export const useNotificationStore = defineStore('notification', () => {
  // State
  const approvals = ref<number>(0);
  const verifications = ref<number>(0);
  const awaitingCp = ref<number>(0);

  // Actions
  async function fetchApprovalCount(): Promise<void> {
    try {
      approvals.value = await documentApi.getAwaitingApprovalCount();
    } catch (error) {
      console.error('Error fetching approval count:', error);
      approvals.value = 0;
    }
  }

  async function fetchVerificationCount(): Promise<void> {
    try {
      verifications.value = await documentApi.getAwaitingVerificationCount();
    } catch (error) {
      console.error('Error fetching verification count:', error);
      verifications.value = 0;
    }
  }

  async function fetchAwaitingCpCount(): Promise<void> {
    try {
      awaitingCp.value = await documentApi.getAwaitingCpCount();
    } catch (error) {
      console.error('Error fetching awaiting CP count:', error);
      awaitingCp.value = 0;
    }
  }

  async function fetchAllCounts(): Promise<void> {
    await Promise.all([fetchApprovalCount(), fetchVerificationCount(), fetchAwaitingCpCount()]);
  }

  function incrementApprovals(): void {
    approvals.value += 1;
  }

  function decrementApprovals(): void {
    if (approvals.value > 0) {
      approvals.value -= 1;
    }
  }

  function incrementVerifications(): void {
    verifications.value += 1;
  }

  function decrementVerifications(): void {
    if (verifications.value > 0) {
      verifications.value -= 1;
    }
  }

  return {
    // State
    approvals,
    verifications,
    awaitingCp,

    // Actions
    fetchApprovalCount,
    fetchVerificationCount,
    fetchAwaitingCpCount,
    fetchAllCounts,
    incrementApprovals,
    decrementApprovals,
    incrementVerifications,
    decrementVerifications,
  };
});
